/**
 * Workstream D — deploy-selective renderer composition.
 *
 * `registerAllRenderers` (./index) is, and stays, the EAGER + TOTAL surface
 * (198 element types) registered via `registry.register("type", Renderer)`
 * calls. The shared `getSharedRegistry()` singleton and the static registration
 * extractor in @cogo/headless both depend on that exact call form, so it must
 * never be refactored into a data table. This module sits ALONGSIDE it and adds
 * the lane-selective composition layer without touching index.ts at all.
 *
 * `RENDERER_TYPE_LANES` declares which render lane each *specialized* renderer
 * belongs to. It is renderer-OWNED (this package knows its own renderers) and is
 * kept in sync with the @cogo/types-v2 contract by the drift guard in
 * `__tests__/render-registry-composition.test.ts`, which validates it against
 * the PUBLIC `inferRenderLanesForElement` — we never import the private
 * `ELEMENT_TYPE_TO_LANES`. Any type NOT listed here is `core-dom`, the always
 * shipped baseline (~150 types: layout, text, slots, semantic aliases).
 *
 * D is registration composition ONLY. Real per-lane bundle splitting — so an
 * unrequested lane's renderer code leaves the bundle entirely — is Workstream E.
 */
import type { RenderLane } from "@cogo/types-v2";
import { ElementRegistry } from "../core/element-registry";
import { registerAllRenderers } from "./index";

const RENDERER_TYPE_LANES: Readonly<Record<string, RenderLane>> = {
  // forms
  form: "forms",
  input: "forms",
  textarea: "forms",
  checkbox: "forms",
  radio: "forms",
  switch: "forms",
  slider: "forms",
  select: "forms",
  "native-select": "forms",
  "input-otp": "forms",
  "chip-input": "forms",
  "phone-input": "forms",
  // overlay
  dialog: "overlay",
  "alert-dialog": "overlay",
  sheet: "overlay",
  drawer: "overlay",
  popover: "overlay",
  "dropdown-menu": "overlay",
  "context-menu": "overlay",
  tooltip: "overlay",
  toast: "overlay",
  "hover-card": "overlay",
  // navigation
  breadcrumb: "navigation",
  pagination: "navigation",
  "navigation-menu": "navigation",
  sidebar: "navigation",
  "sidebar-inset": "navigation",
  "sidebar-trigger": "navigation",
  link: "navigation",
  // data-table
  table: "data-table",
  "data-table": "data-table",
  data_table: "data-table",
  // data-viz
  chart: "data-viz",
  "ring-progress": "data-viz",
  waveform: "data-viz",
  // calendar
  calendar: "calendar",
  "date-picker": "calendar",
  "month-picker": "calendar",
  // single-renderer lanes
  command: "command",
  carousel: "carousel",
  lottie: "lottie",
  // media
  video: "media",
  iframe: "media",
};

/**
 * Render lanes that own at least one *element* renderer, plus the implicit
 * `core-dom` baseline. Game lanes (`game-2d` / `game-3d`) are island lanes —
 * they register zero element renderers — so they are intentionally absent.
 * `composeElementRegistry(ELEMENT_RENDER_LANES)` reproduces `registerAllRenderers`
 * exactly (byte-identical guard).
 */
export const ELEMENT_RENDER_LANES: readonly RenderLane[] = Array.from(
  new Set<RenderLane>(["core-dom", ...Object.values(RENDERER_TYPE_LANES)]),
);

/**
 * Build an ElementRegistry containing ONLY the renderers whose lane is in
 * `lanes`. The `core-dom` baseline is always included (pass
 * `{ includeCoreDom: false }` to opt out). This is the deploy-selective seam:
 * a host derives an app's lanes from its `AppMeta.renderProfile` and composes a
 * registry that omits unrequested specialized renderers, while dev keeps the
 * full `getSharedRegistry()`. Registration is filtered; module bundling is not
 * (that is Workstream E).
 */
export function composeElementRegistry(
  lanes: readonly RenderLane[],
  opts: { includeCoreDom?: boolean } = {},
): ElementRegistry {
  const wanted = new Set<RenderLane>(lanes);
  if (opts.includeCoreDom !== false) wanted.add("core-dom");
  const full = new ElementRegistry();
  registerAllRenderers(full);
  const out = new ElementRegistry();
  for (const type of full.types()) {
    const lane = RENDERER_TYPE_LANES[type] ?? "core-dom";
    if (!wanted.has(lane)) continue;
    const renderer = full.get(type);
    if (renderer) out.register(type, renderer);
  }
  return out;
}
